import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { RegistrationProgressProps } from './types';

const ProgressContainer = styled.div`
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: ${theme.shadows.light};
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ProgressTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
`;

const ProgressText = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const ProgressBar = styled.div`
  background: ${theme.colors.border.light};
  border-radius: 10px;
  height: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProgressFill = styled.div<{ progress: number }>`
  background: ${theme.colors.gradients.primary};
  height: 100%;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Step = styled.div<{ completed: boolean; current: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 12px;
    left: 60%;
    right: -40%;
    height: 2px;
    background: ${props => props.completed 
      ? theme.colors.gradients.primary
      : theme.colors.border.light};
    z-index: 1;
  }
`;

const StepIcon = styled.div<{ completed: boolean; current: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => {
    if (props.completed) return theme.colors.gradients.primary;
    if (props.current) return theme.colors.primary;
    return theme.colors.border.light;
  }};
  color: ${props => props.completed || props.current ? theme.colors.text.light : theme.colors.text.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 0.5rem;
  z-index: 2;
  position: relative;
`;

const StepLabel = styled.span<{ completed: boolean; current: boolean }>`
  font-size: 0.8rem;
  color: ${props => {
    if (props.completed || props.current) return theme.colors.text.primary;
    return theme.colors.text.muted;
  }};
  font-weight: ${props => props.completed || props.current ? '600' : '400'};
  text-align: center;
  line-height: 1.2;
`;

export const RegistrationProgress: React.FC<RegistrationProgressProps> = ({
  currentStep,
  totalSteps,
  steps
}) => {
  const progress = (currentStep / totalSteps) * 100;

  return (
    <ProgressContainer>
      <ProgressHeader>
        <ProgressTitle>Registration Progress</ProgressTitle>
        <ProgressText>{currentStep} of {totalSteps} steps</ProgressText>
      </ProgressHeader>
      
      <ProgressBar>
        <ProgressFill progress={progress} />
      </ProgressBar>
      
      <StepsContainer>
        {steps.map((step, index) => (
          <Step
            key={step.id}
            completed={step.completed}
            current={step.current}
          >
            <StepIcon
              completed={step.completed}
              current={step.current}
            >
              {step.completed ? '✓' : index + 1}
            </StepIcon>
            <StepLabel
              completed={step.completed}
              current={step.current}
            >
              {step.title}
            </StepLabel>
          </Step>
        ))}
      </StepsContainer>
    </ProgressContainer>
  );
};
